import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
	FetchMessages,
	MarkMessagesRead,
	MarkMessageUnread,
	SetMessages,
	SetUserMessages,
} from './../../store/actions/message-center.actions';
import { TxSession } from './../../shared/interfaces/session';
import { getSession } from './../../store/selectors/user.selectors';
import { MessageCenterService } from './../../shared/services/message-center.service';
import { getEmployees } from './../../store/selectors/personal-office.selectors';
import { EmployeeEntity } from './../../store/entities/EmployeeEntity';
import { Component, AfterViewInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { MessageEntity } from '../../store/entities/MessageEntity';
import { getMessageHead, getMessageList } from '../../store/selectors/message-center.selectors';
import { State } from '../../store/reducers';
import { filter, map, take } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TxChatComponent } from '../tx-chat/tx-chat.component';
import { isNumber } from 'util';
import { MessageHead } from '../../store/entities/Api/MessageHead.response';
import { MessageHeadEntity } from '../../store/entities/Api/MessageHeadEntity';

export type MessageCenterSortMode = 'recent' | 'name';

const SORT_MODE_STORAGE_KEY = 'txMessageCenterSort';

/** Falls back to the previous behaviour ("newest first") for an empty or unknown stored value. */
function readStoredSortMode(): MessageCenterSortMode {
	return localStorage.getItem(SORT_MODE_STORAGE_KEY) === 'name' ? 'name' : 'recent';
}

@Component({
	selector: 'tx-messagecenter',
	templateUrl: './tx-messagecenter.component.html',
	styleUrls: ['./tx-messagecenter.component.scss'],
})
export class TxMessageCenterComponent implements AfterViewInit {
	public testData: {
		id: number;
		name: string;
		message: string;
	}[] = [];

	public baseDisplayedColumns = ['direction', 'creation_date', 'message', 'target_persnr', 'actions'];
	public archivedDisplayedColumns = ['direction', 'creation_date', 'message', 'target_persnr', 'archived', 'actions'];

	public displayedColumns = ['direction', 'creation_date', 'message', 'target_persnr', 'actions'];

	public UserMessages$: Observable<MessageEntity[]>;

	public MessageDataSource: MatTableDataSource<MessageEntity> = new MatTableDataSource();

	public ActiveUsers$: Observable<EmployeeEntity[]>;
	public MessageHead$: Observable<MessageHead[]>;

	public CurrentUser$: Observable<TxSession>;

	public Employee: string;
	public Message: string;

	public AnswerMessage: MessageEntity;

	public WithArchived = false;

	/** Contact list order, restored from the last choice made in this browser. */
	public SortMode$ = new BehaviorSubject<MessageCenterSortMode>(readStoredSortMode());

	private employee: string;

	/**
	 * Set once the user manually marked a message unread. Without it the auto-marking below would
	 * immediately read the message again on the next emission of the message list, so the button
	 * would appear to do nothing. Reset when the user switches to another conversation.
	 */
	private skipAutoRead = false;

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild('chat') chat: TxChatComponent;

	constructor(
		private readonly store: Store<State>,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly service: MessageCenterService
	) {
		this.MessageHead$ = this.store.select(getMessageHead);

		this.UserMessages$ = combineLatest([
			router.events
				.pipe(
					filter((event) => event instanceof NavigationEnd),
					map((event: NavigationEnd) => {
						return event.url.split('/').pop();
					})
				)
				.pipe(
					filter((id) => !!id && !isNaN(+id)),
					map((id: string) => {
						this.employee = id;
						// Switching conversations lifts the suppression again, so re-entering a
						// conversation marks it read as usual.
						this.skipAutoRead = false;
						return id;
					}),
					switchMap((id) => this.service.fetchMessages(+id, this.WithArchived).pipe(map((res) => res.messages))),
					tap((res) => {
						setTimeout(() => {
							this.chat.scrollToBottom();
						}, 100);
					})
				)
				.pipe(startWith(null)),
			of(this.router.url.split('/').pop()).pipe(
				switchMap((id) =>
					this.service.fetchMessages(+id, this.WithArchived).pipe(
						filter((res) => res && res.type !== SetUserMessages.type),
						map((res) => res.messages),
						startWith(null)
					)
				)
			),
		]).pipe(
			map((arr) => arr.filter(Boolean).pop()),
			tap((messages) => this.markConversationRead(messages))
		);

		this.ActiveUsers$ = combineLatest([this.store.select(getEmployees), this.MessageHead$, this.SortMode$]).pipe(
			map(([users, heads, sortMode]) =>
				this.sortUsers(users.filter((u) => u.txp_active === 1), heads, sortMode)
			)
		);

		this.CurrentUser$ = this.store.select(getSession);
	}

	ngAfterViewInit(): void {
		if (!isNaN(+this.router.url.split('/').pop())) {
			this.service.fetchMessages(+this.router.url.split('/').pop(), this.WithArchived);
		}

		this.service.fetchMessageHead().pipe(take(1)).subscribe();

		this.MessageDataSource.sort = this.sort;
	}

	sendMessage(message: string, answer: MessageEntity) {
		this.service.sendMessage(+this.employee, message, +this.router.url.split('/').pop(), answer);
	}

	/*getEmployeeName(persnr: number, users: EmployeeEntity[]) {
		const user = users.find((u) => u.persnr === `${persnr}`);
		return user ? `${user.firstname} ${user.lastname}` : persnr;
	}*/

	markMessageUnread(message: MessageEntity) {
		const persnr = this.currentEmployeeId();

		// NaN would be serialised as null and the webservice rejects the whole request with 400.
		if (isNaN(persnr)) {
			return;
		}

		// Has to come before the dispatch: refreshing the head raises the unread count again, and
		// without this the auto-marking would read the message straight back.
		this.skipAutoRead = true;
		this.store.dispatch(MarkMessageUnread(persnr, message.id));
	}

	/**
	 * The employee whose mailbox is on screen, taken from the route like archiveMessage does.
	 * `this.employee` is only filled by the navigation branch of UserMessages$ and is still
	 * undefined right after the component is created.
	 */
	private currentEmployeeId(): number {
		return +this.router.url.split('/').pop();
	}

	archiveMessage(element: MessageEntity, remove: boolean = false) {
		this.service.archiveMessage(element.id, +this.router.url.split('/').pop(), this.WithArchived, remove);
	}

	/*refetchMessages() {
		this.store.dispatch(FetchMessages(0, this.WithArchived));
		if (this.WithArchived) {
			this.displayedColumns = this.archivedDisplayedColumns;
		} else {
			this.displayedColumns = this.baseDisplayedColumns;
		}
	}*/

	refresh() {
		this.store.dispatch(FetchMessages(+this.employee, this.WithArchived));
	}

	/**
	 * Marks the messages of the conversation that was just opened as read.
	 *
	 * How many are unread is only known from the head's `unread_messages` — the per-message
	 * `message_read` field is not filled by the server in a way we can rely on. So take that
	 * count and mark exactly the newest N incoming messages, which also keeps the legacy backlog
	 * (everything predating this feature) untouched instead of firing hundreds of requests.
	 *
	 * `target_persnr === 0` is what "incoming" means here: addressed to the office rather than
	 * sent by it. Those are the ones the unread count is built from.
	 *
	 * Self-limiting: the effect refreshes the head afterwards, so the count drops to 0 and the
	 * next emission marks nothing.
	 */
	private markConversationRead(messages: MessageEntity[]) {
		const persnr = this.currentEmployeeId();

		if (this.skipAutoRead || !messages?.length || isNaN(persnr)) {
			return;
		}

		this.store
			.select(getMessageHead)
			.pipe(take(1))
			.subscribe((heads) => {
				const unreadCount = heads?.find((head) => +head.persnr === persnr)?.unread_messages ?? 0;

				if (unreadCount <= 0) {
					return;
				}

				const ids = messages
					.filter((message) => message.target_persnr === 0)
					.sort((a, b) => new Date(b.creation_date).getTime() - new Date(a.creation_date).getTime())
					.slice(0, unreadCount)
					.map((message) => message.id);

				if (ids.length) {
					this.store.dispatch(MarkMessagesRead(persnr, ids));
				}
			});
	}

	setSortMode(mode: MessageCenterSortMode) {
		localStorage.setItem(SORT_MODE_STORAGE_KEY, mode);
		this.SortMode$.next(mode);
	}

	private sortUsers(
		users: EmployeeEntity[],
		heads: MessageHeadEntity[],
		mode: MessageCenterSortMode
	): EmployeeEntity[] {
		if (mode === 'name') {
			// localeCompare so umlauts sort where a German reader expects them; getLabel falls back
			// to the persnr when a name is missing, hence the String(). Label is
			// "Nachname, Vorname", so this sorts by Nachname.
			return users
				.slice()
				.sort((a, b) => String(this.getLabel(a)).localeCompare(String(this.getLabel(b)), undefined, { numeric: true }));
		}

		return this.sortByLatestMessage(users, heads);
	}

	/**
	 * Orders the contact list so the most recent conversation is on top: contacts that have
	 * messages come first, newest last_message_date first, everyone else keeps persnr order.
	 *
	 * `heads` is null until the first getMessageHead response arrives — the list then simply
	 * renders in persnr order instead of being withheld, and re-sorts once the heads land.
	 */
	private sortByLatestMessage(users: EmployeeEntity[], heads: MessageHeadEntity[]): EmployeeEntity[] {
		const lastMessageAt = new Map<number, number>();
		(heads ?? []).forEach((head) => {
			const timestamp = new Date(head.last_message_date).getTime();
			if (!isNaN(timestamp)) {
				lastMessageAt.set(+head.persnr, timestamp);
			}
		});

		return users.slice().sort((a, b) => {
			const aAt = lastMessageAt.get(+a.persnr);
			const bAt = lastMessageAt.get(+b.persnr);

			if (aAt !== undefined && bAt !== undefined) {
				return bAt - aAt;
			}
			if (aAt !== undefined) {
				return -1;
			}
			if (bAt !== undefined) {
				return 1;
			}

			return +a.persnr - +b.persnr;
		});
	}

	// "Nachname, Vorname" — same display order as team calendar, workflow etc.
	getLabel(user: EmployeeEntity) {
		return user.firstname && user.lastname ? `${user.lastname}, ${user.firstname}` : user.persnr;
	}

	getBadges(head: MessageHeadEntity[]) {
		return (
			head?.reduce((acc, cur) => {
				acc[cur.persnr] = cur.unread_messages;
				return acc;
			}, {}) ?? {}
		);
	}

	getAvailableHeight() {
		return window.innerHeight - 263;
	}
}
